import { useState } from 'react';
import { BottomSheet } from '../common/BottomSheet';
import { PhotoCapture } from './PhotoCapture';
import { PhotoViewer } from './PhotoViewer';
import { usePhotos } from '../../hooks/usePhotos';
import { useOffline } from '../../hooks/useOffline';
import { useToast } from '../../context/ToastContext';
import { addPhoto, setPrimaryPhoto, updatePhotoCaption, deletePhoto } from '../../services/photos';
import type { PlantPhoto } from '../../types/photo';

interface PhotoGalleryProps {
  userId: string;
  plantId: string;
  plantName: string;
  /**
   * The plant's current `photoURL`. Shown as a single placeholder thumbnail for
   * plants whose gallery hasn't been written to yet — their photo only becomes
   * a real photo doc on the first add (lazy backfill in `addPhoto`).
   */
  fallbackPhotoURL: string;
}

/**
 * Horizontal strip of a plant's photos, oldest first, so it reads as a growth
 * timeline. Tapping a photo opens the full-screen viewer; the trailing tile
 * adds a new one.
 */
export function PhotoGallery({ userId, plantId, plantName, fallbackPhotoURL }: PhotoGalleryProps) {
  const { photos, loading } = usePhotos(userId, plantId);
  const { isOffline } = useOffline();
  const { showSuccess, showError } = useToast();
  const [captureOpen, setCaptureOpen] = useState(false);
  const [viewing, setViewing] = useState<PlantPhoto | null>(null);
  const [uploadingPreview, setUploadingPreview] = useState<string | null>(null);

  // Keep the open viewer in sync with live updates (caption saved, key photo
  // changed) instead of showing the snapshot it was opened with.
  const viewingPhoto = viewing ? photos.find((p) => p.id === viewing.id) ?? null : null;

  const handleCapture = async (file: File, previewUrl: string) => {
    setCaptureOpen(false);

    // Fail fast when offline. Firebase Storage retries a failed upload with a
    // long backoff, so without this the spinner sits there for minutes and the
    // user is never told anything went wrong.
    if (isOffline) {
      showError("You're offline. Connect to the internet to add a photo.");
      return;
    }

    setUploadingPreview(previewUrl);

    const result = await addPhoto(userId, plantId, file);

    setUploadingPreview(null);
    if (result.success) {
      showSuccess('Photo added');
    } else {
      showError(result.error);
    }
  };

  const handleSetPrimary = async (photoId: string) => {
    const result = await setPrimaryPhoto(userId, plantId, photoId);
    if (result.success) {
      showSuccess('Key photo updated');
      setViewing(null);
    } else {
      showError(result.error);
    }
  };

  const handleSaveCaption = async (photoId: string, caption: string) => {
    const result = await updatePhotoCaption(userId, plantId, photoId, caption);
    if (result.success) showSuccess('Caption saved');
    else showError(result.error);
  };

  const handleDelete = async (photoId: string) => {
    const result = await deletePhoto(userId, plantId, photoId);
    if (result.success) {
      showSuccess('Photo deleted');
      setViewing(null);
    } else {
      showError(result.error);
    }
  };

  // Before the first gallery write a plant has no photo docs, only photoURL.
  const showFallbackTile = !loading && photos.length === 0 && !!fallbackPhotoURL;

  return (
    <section aria-labelledby="photo-gallery-heading">
      <div className="mb-3 flex items-center gap-2">
        <span className="h-px flex-1 bg-gray-200" />
        <h2 id="photo-gallery-heading" className="section-header">Photos</h2>
        <span className="h-px flex-1 bg-gray-200" />
      </div>

      <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-1">
        {loading && (
          <div className="h-20 w-20 shrink-0 animate-shimmer rounded-lg" aria-hidden="true" />
        )}

        {showFallbackTile && (
          <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-gray-200">
            <img
              src={fallbackPhotoURL}
              alt={`${plantName} - user photo`}
              className="h-full w-full object-cover"
              loading="lazy"
            />
            <span className="absolute bottom-1 left-1 rounded-full bg-primary px-1.5 py-0.5 font-sans text-[10px] font-bold uppercase text-editorial-black">
              Key
            </span>
          </div>
        )}

        {photos.map((photo) => (
          <button
            key={photo.id}
            onClick={() => setViewing(photo)}
            className="relative h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-gray-200 transition-transform hover:scale-105"
            aria-label={
              photo.caption
                ? `View photo: ${photo.caption}`
                : `View photo of ${plantName}${photo.isPrimary ? ' (key photo)' : ''}`
            }
          >
            <img
              src={photo.url}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
            />
            {photo.isPrimary && (
              <span className="absolute bottom-1 left-1 rounded-full bg-primary px-1.5 py-0.5 font-sans text-[10px] font-bold uppercase text-editorial-black">
                Key
              </span>
            )}
          </button>
        ))}

        {uploadingPreview && (
          <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-gray-200">
            <img src={uploadingPreview} alt="" className="h-full w-full object-cover opacity-50" />
            <div className="absolute inset-0 flex items-center justify-center bg-white/50">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          </div>
        )}

        <button
          onClick={() => setCaptureOpen(true)}
          disabled={!!uploadingPreview}
          className="flex h-20 w-20 shrink-0 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-gray-300 text-gray-500 transition-colors hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
          aria-label={`Add a photo of ${plantName}`}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 24 }} aria-hidden="true">
            add_a_photo
          </span>
          <span className="font-sans text-[10px] font-bold uppercase tracking-wide">Add</span>
        </button>
      </div>

      <div role="status" aria-live="polite" className="sr-only">
        {uploadingPreview ? 'Uploading photo' : ''}
      </div>

      <BottomSheet
        open={captureOpen}
        onOpenChange={setCaptureOpen}
        title="Add a photo"
        description={`Capture how ${plantName} looks today`}
        initialSnap="half"
      >
        <PhotoCapture onCapture={handleCapture} onError={showError} />
      </BottomSheet>

      <PhotoViewer
        photo={viewingPhoto}
        plantName={plantName}
        onClose={() => setViewing(null)}
        onSetPrimary={handleSetPrimary}
        onSaveCaption={handleSaveCaption}
        onDelete={handleDelete}
      />
    </section>
  );
}
